import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from '../hooks/use-toast';
import { ArrowLeft, Phone, Mail, MapPin, Send } from 'lucide-react';
import { BrandLogo } from './BrandLogo';

const ContactUsPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast({ title: 'Fill all fields', description: 'Please complete the form before sending', variant: 'destructive' });
      return;
    }
    setSent(true);
    toast({ title: 'Message received', description: "Thanks for reaching out — our team will get back to you shortly." });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="contact-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <BrandLogo variant="compact" className="h-8 w-auto" />
          <h1 className="text-xl font-bold border-l pl-4">Contact Us</h1>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-3xl grid md:grid-cols-2 gap-6">
        <Card className="p-6 space-y-5">
          <h2 className="text-xl font-bold">Get in Touch</h2>
          <div className="flex items-start gap-3">
            <Phone className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="font-medium">Phone Support</p>
              <p className="text-sm text-muted-foreground">+91 1800-123-4567 (9 AM – 9 PM)</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="font-medium">Email</p>
              <p className="text-sm text-muted-foreground">support@bookmydriver.in</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-primary mt-0.5" />
            <div>
              <p className="font-medium">Registered Office</p>
              <p className="text-sm text-muted-foreground">V8 Technologies Private Limited, India</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">You can also reach us anytime via the in-app chat support on your dashboard.</p>
        </Card>

        <Card className="p-6">
          {sent ? (
            <div className="text-center py-8" data-testid="contact-sent-note">
              <Send className="w-10 h-10 text-green-600 mx-auto mb-3" />
              <p className="font-semibold">Message sent!</p>
              <p className="text-sm text-muted-foreground mt-1">We'll respond to {form.email} soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="contact-name">Your Name</Label>
                <Input id="contact-name" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  className="h-11" data-testid="contact-name-input" />
              </div>
              <div>
                <Label htmlFor="contact-email">Email</Label>
                <Input id="contact-email" type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  className="h-11" data-testid="contact-email-input" />
              </div>
              <div>
                <Label htmlFor="contact-message">Message</Label>
                <Textarea id="contact-message" rows={5} value={form.message}
                  onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))} data-testid="contact-message-input" />
              </div>
              <Button type="submit" className="w-full bg-primary hover:bg-primary-dark" data-testid="contact-submit-btn">
                Send Message
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ContactUsPage;
